import { Component, Input, forwardRef, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploadService } from '../../services/file-upload.service';

export interface Photo {
  fileName: string;
  fileUrl: string;
  blobName?: string; // 新增：用於生成預覽連結
  previewUrl?: string; // 新增：實際的預覽連結
}

@Component({
  selector: 'app-form-photo-upload',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="form-section">
      <label [for]="id" [class.required-field]="required">{{ label }}</label>
      <input
        type="file"
        [id]="id"
        [name]="name"
        (change)="handlePhotoUpload($event)"
        accept="image/*"
        multiple
      />
      <div class="photo-preview">
        <div *ngFor="let photo of photos; let i = index" class="photo-item">
          <div *ngIf="!photo.previewUrl" class="loading-placeholder">
            <i class="fa fa-spinner fa-spin"></i>
            <span>載入中...</span>
          </div>
          <img *ngIf="photo.previewUrl" [src]="photo.previewUrl" alt="Preview" />
          <button type="button" (click)="removePhoto(i)">X</button>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormPhotoUploadComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss'] // Add custom styling
})
export class FormPhotoUploadComponent implements ControlValueAccessor {
  @Input() label: string = '缺失照片 (可上傳多張，並可刪除)';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() required: boolean = false; // 是否必填

  @Output() photosChange = new EventEmitter<Photo[]>();

  photos: Photo[] = [];

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(private fileUploadService: FileUploadService) {}

  // Handle photo upload and push the uploaded photo URL
  handlePhotoUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      files.forEach((file) => {
        // Upload file using FileUploadService
        this.fileUploadService.uploadFile(file).subscribe({
          next: (response) => {
            if (response.type === 'success' && response.response) {
              console.log("response",response);
              const blobName = response.response.data.blobName ;
              const photoItem: Photo = {
                fileName: file.name,
                fileUrl: blobName,  // 儲存原始連結
                blobName: blobName  // 儲存 blob 名稱用於生成預覽連結
              };

              console.log("photoItem",photoItem);

              // 生成預覽連結
              this.generatePreviewUrl(photoItem);

              this.photos.push(photoItem);  // Add to photo array
              this.photosChange.emit(this.photos);  // Emit changes
              this.onChange(this.photos);  // Update model
              this.onTouched();  // Mark as touched for validation
            }
          },
          error: (error) => {
            console.error('Error uploading file:', error);
          }
        });
      });
    }
  }

  // Remove photo from list
  removePhoto(index: number): void {
    this.photos.splice(index, 1);
    this.photosChange.emit(this.photos);  // Emit changes
    this.onChange(this.photos);  // Update model
    this.onTouched();  // Mark as touched for validation
  }


  //TODO:所有有用到Azure Blob Storage 的地方都要統一巡過跟處理，現在有太多種作法了
  // Generate preview URL for photo
  private generatePreviewUrl(photo: Photo): void {
    if (photo.blobName) {
      this.fileUploadService.generateViewUrl(photo.blobName).subscribe({
        next: (response) => {

          console.log("generateViewUrl response",response);

          if (response && response.data.viewUrl) {
            photo.previewUrl = response.data.viewUrl;
          }
        },
        error: (error) => {
          console.error('Error generating preview URL:', error);
          // 如果生成預覽連結失敗，使用原始連結作為備用
          photo.previewUrl = photo.fileUrl;
        }
      });
    } else {
      // 如果沒有 blobName，使用原始連結
      photo.previewUrl = photo.fileUrl;
    }
  }

  // Implement ControlValueAccessor
  writeValue(value: Photo[]): void {
    if (value) {
      this.photos = value;
      // 為每個照片生成預覽連結（如果還沒有的話）
      this.photos.forEach(photo => {
        if (!photo.previewUrl) {
          this.generatePreviewUrl(photo);
        }
      });
    } else {
      this.photos = [];
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Handle disabled state if needed
  }
}
